import {
  Form,
  message,
  Select,
  Button,
  Card,
  Input,
  DatePicker,
  Flex,
  Spin,
} from "antd";
import React, { useEffect, useState } from "react";
import certificationService from "../../services/certification.service";
import { useParams } from "react-router-dom";
import { PageHeader } from "@ant-design/pro-components";
import { FeatureModificator } from "./Components/FeatureModificator";
import {
  ModificationsProvider,
  useModifications,
} from "./Hooks/useModifications";

import { FilePdfOutlined, DownloadOutlined } from "@ant-design/icons";
import { downloadPDF } from "../../Helpers/Functions";
import { modificationNotes } from "./Helpers/modificationNotes";
import { createDate, formatDate } from "../../Helpers/time-utils";

const RECTIFICATION_DEFAULT_NOTE =
  "EL VEHÍCULO INSPECCIONADO NO HA SIDO OBJETO DE MODIFICACIÓN ALGUNA";

const ADECUACION_DEFAULT_NOTE = `(ACORDE A LA DIRECTIVA N° 002-2006-MTC-15 , APROBADA POR RESOLUCIÓN
DIRECTORAL N° 4848-2006-MTC-15 Y MODIFICADA CON LA R.D 10476-2008-MTC/15)`;

const { Option } = Select;

const noteOptions = modificationNotes.map((note) => ({
  label: note,
  value: note, 
}));

const ModificationGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [required, setRequired] = useState(false);

  const [form] = Form.useForm();
  let { id } = useParams();

  const {
    options,
    featureGroup,
    addGroup,
    updateNotes,
    isFetching,
    certification,
    refetch,
  } = useModifications();

  const updateModification = async () => {
    const RECTIFICATION_ID = 2;
    const { observation, date, certificate_number } = form.getFieldsValue();

    const groups = featureGroup.map((feature) => {
      if (feature.type == RECTIFICATION_ID) {
        return { ...feature, observation: ADECUACION_DEFAULT_NOTE };
      }
      return feature;
    });

    const res = await certificationService.updateModifications(id, {
      observation,
      date,
      certificate_number,
      items: groups,
    });

    return res;
  };

  const finish = async () => {
    const res = await updateModification();
    if (!res.success) {
      message.error(res.message);
      return;
    }
    message.success(res.message);
  };

  const certificateNow = async () => {
    setRequired(true);
    setTimeout(async () => {
      form
        .validateFields()
        .then(async (values) => {
          setLoading(true);
          await updateModification();
          const res = await certificationService.certificate(id);
          setLoading(false);
          if (!res.success) {
            message.error(res.message);
            return;
          }
          message.success(res.message);

          refetch();
        })
        .catch((err) => {
          setLoading(false);
          message.warning(err?.message || "¡Por favor completa los campos!");
        });
    }, 150);
  };

  const removeModel = () => {};

  const isRectification = () => {
    if (featureGroup.length == 1) {
      return featureGroup.findIndex((feature) => feature.type == 4) > -1;
    }

    return false;
  };

  useEffect(() => {
    const fieldNote = form.getFieldValue("observation");
    if (isRectification()) {
      if (!fieldNote) {
        form.setFieldsValue({ observation: RECTIFICATION_DEFAULT_NOTE });
      }
    }
    if (fieldNote == RECTIFICATION_DEFAULT_NOTE) {
      form.setFieldsValue({ observation: "" });
    }
  }, [featureGroup]);

  useEffect(() => {
    if (certification) {
      form.setFieldsValue({
        certificate_number: certification.certificate_number ?? "DG-26-0",
        date: createDate(certification.date) ?? createDate(),
        observation: certification.observation,
      });
    }
  }, [certification, form]);

  const getData = () => {
    const data = form.getFieldsValue();
    data.name = options.find((option) => option.value === data.type)?.label;
    return data;
  };

  if (options.length == 0) {
    return <Spin spinning />;
  }

  return (
    <React.Fragment>
      <PageHeader
        ghost={false}
        onBack={() => window.history.back()}
        title={`Modificaciones: ${certification?.plate} / ${
          certification?.inspection_number ?? ""
        }`}
        subTitle={formatDate(createDate(), "LLL")}
        extra={[
          certification?.status === "finished" && (<> 
            <Button
              icon={<FilePdfOutlined />}
              key="show"
              onClick={() =>
                downloadPDF(
                  true,
                  certification?.certificate_number,
                  certification?.pdf_url
                )
              }
              loading={loading || isFetching}
            >
              Ver
            </Button>

             <Button
              icon={<FilePdfOutlined />}
              key="show"
              onClick={() =>
                downloadPDF(
                  true,
                  certification?.certificate_number,
                  `${certification?.pdf_url}?extra=true`
                )
              }
              loading={loading || isFetching}
            >
              Extra
            </Button>

            </>

          ),

          certification?.status === "finished" && (
            <Button
              icon={<DownloadOutlined />}
              key="download"
              onClick={() =>
                downloadPDF(
                  false,
                  certification?.certificate_number,
                  certification?.pdf_url
                )
              }
              loading={loading || isFetching}
            >
              Descargar
            </Button>
          ),
          <Button onClick={removeModel} danger key="cancel">
            Anular
          </Button>,
          <Button
            key="modifications"
            loading={loading}
            onClick={certificateNow}
          >
            Certificar
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Guardar
          </Button>,
        ]}
      />

      <Form
        layout="vertical"
        form={form}
        name="control-hooks"
        initialValues={{ chassis_status: "NUEVO", type: 1  ,note_options: '' }}
        onFinish={finish}
      >
        <Flex gap={10} justify="space-between" style={{ width: "100%" }}>
          <div style={{ flex: 1 }}>
            <Card className="cardex" title="Datos del certificado">
              <Form.Item
                style={{ width: 200 }}
                name="certificate_number"
                label="Número de Certificado"
                rules={[
                  {
                    required: true,
                    message: "Ingresa el numero de certificado",
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  aria-autocomplete="none"
                  size="middle"
                />
              </Form.Item>

              <Form.Item
                style={{ marginTop: 10 }}
                name="date"
                label="Fecha de certificación"
                rules={[{ required: true, message: "Ingresa una fecha" }]}
              >
                <DatePicker style={{ width: 240 }} size="middle" />
              </Form.Item>

              <Form.Item
               initialValue={''}
                style={{maxWidth: 800}}
                name="note_options"
                label="Selecciona nota predeterminada"
                rules={[{ required: false }]}
              >
                <Select options={[{value: '', 'label': 'Selecciona'}, ...noteOptions]} onChange={(value)=> {

                    form.setFieldsValue({observation: form.getFieldValue('observation') +"\n" +value})
                }} />
              </Form.Item>

              <Form.Item
                name="observation"
                label="Observaciones"
                rules={[{ required: false }]}
              >
                <Input.TextArea size="middle" />
              </Form.Item>
            </Card>
          </div>

          <Card loading={isFetching} className="cardex" title="Agregar">
            <Form.Item
              name="type"
              label="Tipo de modificación"
              rules={[{ required: true }]}
            >
              <Select style={{ width: 180 }} options={options} />
            </Form.Item>

            <Form.Item
              name="chassis_status"
              style={{ maxWidth: 400 }}
              label="Estado de chasis"
              rules={[{ required: true }]}
            >
              <Select size="middle">
                <Option value="NUEVO">NUEVO</Option>
                <Option value="USADO">USADO</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                onClick={() => addGroup(getData())}
                type="primary"
                ghost
                size="middle"
              >
                Agregar
              </Button>
            </Form.Item>
          </Card>
        </Flex>
      </Form>
      {featureGroup.map((feature) => (
        <FeatureModificator
          key={feature.type}
          updateNotes={updateNotes}
          featureCategory={feature}
        />
      ))}
    </React.Fragment>
  );
};

const ModificationBase = () => (
  <ModificationsProvider>
    <ModificationGenerator />
  </ModificationsProvider>
);

export default ModificationBase;
